import { useEffect, useState } from "react";
import { FingerPrintIcon } from "@heroicons/react/24/outline";
import Modal, { ModalActions } from "@/common/Modal";
import Button from "@/common/Button";
import { toast } from "react-hot-toast";
import { useUser } from "@/hooks/use-user.ts";
import { useDismissDialogMutation } from "@/services/apis/api.ts";
import { UserDismissableDialog } from "@/types/generated/management-api-types";
import { useTermsAcceptancePending } from "@/features/Terms/TermsAcceptanceGuard.tsx";
import {
    isPasskeySupported,
    listPasskeys,
    registerPasskey,
} from "@/app/auth/passkey.ts";
import {
    SignInWithoutPasskey,
    takeSignInWithoutPasskey,
} from "@/app/auth/passkeyNudge.ts";

const DIALOG_ID: UserDismissableDialog = "passkey-enrollment";

/** What the passkey replaces, for each way of signing in without one. */
const INSTEAD_OF: Record<SignInWithoutPasskey, string> = {
    code: "waiting for an email code",
    password: "typing your password",
};

/**
 * Offers a passkey right after a sign-in that took an email code or a
 * password, whenever the account has no passkey yet. "Not now" only closes it,
 * so the next such sign-in asks again; "Don't ask again" dismisses it for good
 * through UserDismissableDialog. Google and passkey sign-ins aren't asked (see
 * passkeyNudge.ts), and neither are browsers without WebAuthn. It waits for the
 * terms modal rather than open on top of it.
 */
export default function PasskeyEnrollmentPrompt() {
    const user = useUser();
    const [dismissDialog] = useDismissDialogMutation();
    const termsPending = useTermsAcceptancePending();
    const [signedInWith, setSignedInWith] =
        useState<SignInWithoutPasskey>("code");
    const [open, setOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const wantsPrompt = user.hasDialog(DIALOG_ID);

    useEffect(() => {
        if (termsPending) {
            return;
        }
        // Taken before any other check, so a sign-in that isn't asked about
        // leaves nothing behind for a later one.
        const method = takeSignInWithoutPasskey();
        if (!method || !isPasskeySupported() || !wantsPrompt) {
            return;
        }
        listPasskeys()
            .then((passkeys) => {
                if (passkeys.length === 0) {
                    setSignedInWith(method);
                    setOpen(true);
                }
            })
            .catch((error) => {
                console.error("Passkey prompt check failed:", error);
            });
    }, [termsPending, wantsPrompt]);

    const close = () => setOpen(false);

    const dontAskAgain = () => {
        dismissDialog({ id: DIALOG_ID });
        close();
    };

    const handleSetup = async () => {
        setIsAdding(true);
        try {
            await registerPasskey();
            toast.success("Passkey added. Use it the next time you sign in.");
            close();
        } catch (error) {
            // Amplify reports every WebAuthn NotAllowedError this way: the
            // browser's own dialog was closed, timed out or was refused. Ours
            // stays open, to try again or say not now.
            if (
                error instanceof Error &&
                error.name === "PasskeyRegistrationCanceled"
            ) {
                return;
            }
            console.error("Failed to add passkey:", error);
            toast.error("Could not add passkey. Please try again.");
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={close}
            title={
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500/10">
                        <FingerPrintIcon className="h-6 w-6 text-indigo-500" />
                    </div>
                    Sign in faster next time
                </div>
            }
            description={
                <span className="block text-center">
                    Add a passkey to sign in with Face ID, Touch ID or a
                    security key, instead of {INSTEAD_OF[signedInWith]}.
                </span>
            }
        >
            <ModalActions>
                <Button plain onClick={dontAskAgain}>
                    Don’t ask again
                </Button>
                <Button plain onClick={close}>
                    Not now
                </Button>
                <Button type="primary" onClick={handleSetup} loading={isAdding}>
                    Add passkey
                </Button>
            </ModalActions>
        </Modal>
    );
}
